import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ArrowLeft, XCircle, CheckSquare, Square, XOctagon, RefreshCw, BarChart3, Clock, Users, Activity, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import AccountsManager from './AccountsManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Backoffice() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [activeTab, setActiveTab] = useState('sessions');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (!token || !user) {
      toast.error('Acesso negado: faca login primeiro');
      navigate('/login');
      return;
    }
    const userData = JSON.parse(user);
    if (userData.role !== 'admin') {
      toast.error('Acesso negado: apenas administradores');
      navigate('/host');
      return;
    }
    fetchSessions();
  }, [navigate]);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Erro ao carregar sessoes');
      setLoading(false);
    }
  };

  const fetchLogs = async (sessionId) => {
    setLoadingLogs(true);
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/logs`);
      setLogs(response.data);
      setLoadingLogs(false);
    } catch (error) {
      toast.error('Erro ao carregar logs');
      setLoadingLogs(false);
    }
  };

  const handleSessionClick = (session) => {
    setSelectedSession(session);
    fetchLogs(session.id);
    fetchMetrics(session.id);
  };

  const fetchMetrics = async (sessionId) => {
    setLoadingMetrics(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions/${sessionId}/metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(response.data);
    } catch (error) {
      setMetrics(null);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const stopSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/sessions/${sessionId}/stop`);
      setSessions((prev) =>
        prev.map((s) => s.id === sessionId ? { ...s, status: 'stopped' } : s)
      );
      if (selectedSession?.id === sessionId) {
        setSelectedSession((prev) => ({ ...prev, status: 'stopped' }));
      }
      toast.success('Sessao encerrada');
    } catch (error) {
      toast.error('Erro ao encerrar sessao');
    }
  };

  const reopenSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/sessions/${sessionId}/reopen`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions((prev) =>
        prev.map((s) => s.id === sessionId ? { ...s, status: 'created' } : s)
      );
      if (selectedSession?.id === sessionId) {
        setSelectedSession((prev) => ({ ...prev, status: 'created' }));
      }
      toast.success('Sessao reaberta');
    } catch (error) {
      toast.error('Erro ao reabrir sessao');
    }
  };

  const toggleSelect = (e, sessionId) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const selectAllActive = () => {
    const activeIds = sessions.filter(s => s.status === 'created' || s.status === 'active').map(s => s.id);
    setSelectedIds(new Set(activeIds));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const stopAllSessions = async () => {
    if (!window.confirm('Tem a certeza que quer fechar TODAS as sessoes ativas?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/sessions/stop-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.count} sessoes encerradas`);
      fetchSessions();
      setSelectedIds(new Set());
    } catch (error) {
      toast.error('Erro ao encerrar sessoes');
    }
  };

  const stopSelectedSessions = async () => {
    if (selectedIds.size === 0) { toast.error('Selecione pelo menos uma sessao'); return; }
    if (!window.confirm(`Fechar ${selectedIds.size} sessao(oes) selecionada(s)?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/sessions/stop-selected`, {
        session_ids: Array.from(selectedIds)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${res.data.count} sessoes encerradas`);
      fetchSessions();
      setSelectedIds(new Set());
    } catch (error) {
      toast.error('Erro ao encerrar sessoes');
    }
  };

  const activeSessions = sessions.filter(s => s.status === 'created' || s.status === 'active');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <img src="/logo-dih.png" alt="Do IT Happen" className="h-10" />
            <h1 className="font-['Manrope'] text-3xl md:text-4xl font-bold" data-testid="backoffice-title">
              Backoffice
            </h1>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="text-slate-400 hover:text-white"
            data-testid="back-home-backoffice-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-800">
          <button
            onClick={() => setActiveTab('sessions')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'sessions' ? 'border-cyan-500 text-white' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
            data-testid="tab-sessions">
            <Activity className="w-4 h-4 inline mr-2" /> Sessões
          </button>
          <button
            onClick={() => setActiveTab('accounts')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'accounts' ? 'border-cyan-500 text-white' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
            data-testid="tab-accounts">
            <Building2 className="w-4 h-4 inline mr-2" /> Contas
          </button>
        </div>

        {activeTab === 'accounts' && <AccountsManager />}

        {activeTab === 'sessions' && (<>
        {/* Bulk actions bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Button
            size="sm"
            variant="destructive"
            onClick={stopAllSessions}
            disabled={activeSessions.length === 0}
            className="bg-red-700 hover:bg-red-600"
            data-testid="stop-all-sessions-btn"
          >
            <XOctagon className="w-4 h-4 mr-2" />
            Fechar Todas ({activeSessions.length})
          </Button>
          <Button
            size="sm"
            onClick={stopSelectedSessions}
            disabled={selectedIds.size === 0}
            className="bg-orange-700 hover:bg-orange-600 text-white"
            data-testid="stop-selected-sessions-btn"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Fechar Selecionadas ({selectedIds.size})
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={selectAllActive}
            className="text-slate-400 hover:text-white"
            data-testid="select-all-btn"
          >
            <CheckSquare className="w-4 h-4 mr-2" />
            Selecionar Ativas
          </Button>
          {selectedIds.size > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              className="text-slate-400 hover:text-white"
              data-testid="clear-selection-btn"
            >
              Limpar Selecao
            </Button>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Sessions List */}
          <Card className="bg-slate-900 border-slate-800" data-testid="sessions-list-card">
            <CardHeader>
              <CardTitle className="font-['Manrope'] text-xl text-white">
                Sessoes ({sessions.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sessions.length === 0 ? (
                <p className="font-['Inter'] text-slate-400 text-center py-8" data-testid="no-sessions-message">
                  Nenhuma sessao encontrada
                </p>
              ) : (
                <div className="space-y-3">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      onClick={() => handleSessionClick(session)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                        selectedSession?.id === session.id
                          ? 'border-blue-500 bg-blue-950/50'
                          : selectedIds.has(session.id)
                          ? 'border-orange-500 bg-orange-950/30'
                          : 'border-slate-700 hover:border-slate-600 hover:bg-slate-800'
                      }`}
                      data-testid="session-item"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-3">
                          {/* Checkbox */}
                          {(session.status === 'created' || session.status === 'active') && (
                            <button
                              onClick={(e) => toggleSelect(e, session.id)}
                              className="text-slate-400 hover:text-white transition-colors"
                              data-testid={`select-session-${session.code}`}
                            >
                              {selectedIds.has(session.id) ? (
                                <CheckSquare className="w-5 h-5 text-orange-400" />
                              ) : (
                                <Square className="w-5 h-5" />
                              )}
                            </button>
                          )}
                          <span className="font-['JetBrains_Mono'] text-lg font-bold text-blue-400" data-testid="session-item-code">
                            {session.code}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              session.status === 'active'
                                ? 'bg-green-900/50 text-green-400 border border-green-700'
                                : session.status === 'stopped'
                                ? 'bg-red-900/50 text-red-400 border border-red-700'
                                : session.status === 'archived'
                                ? 'bg-slate-700/50 text-slate-400 border border-slate-600'
                                : 'bg-yellow-900/50 text-yellow-400 border border-yellow-700'
                            }`}
                            data-testid="session-item-status"
                          >
                            {session.status === 'active' ? 'Ativa' : session.status === 'stopped' ? 'Parada' : session.status === 'archived' ? 'Arquivada' : 'Criada'}
                          </span>
                          {(session.status === 'created' || session.status === 'active') && (
                            <button
                              onClick={(e) => stopSession(e, session.id)}
                              className="text-red-400 hover:text-red-300 transition-colors p-1 rounded hover:bg-red-900/30"
                              title="Encerrar sessao"
                              data-testid={`stop-session-${session.code}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                          {session.status === 'stopped' && (
                            <button
                              onClick={(e) => reopenSession(e, session.id)}
                              className="text-amber-400 hover:text-amber-300 transition-colors p-1 rounded hover:bg-amber-900/30"
                              title="Reabrir sessao"
                              data-testid={`reopen-session-${session.code}`}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="font-['Inter'] text-sm text-slate-400 space-y-1">
                        <p>
                          <span className="font-medium text-slate-300">Host:</span> {session.user_name}
                        </p>
                        <p>
                          <span className="font-medium text-slate-300">Idiomas:</span> {session.source_lang} {'\u2192'} {session.target_langs.join(', ')}
                        </p>
                        <p>
                          <span className="font-medium text-slate-300">Criada:</span>{' '}
                          {new Date(session.created_at).toLocaleString('pt-PT')}
                        </p>
                        {session.scheduled_for && (
                          <p>
                            <span className="font-medium text-yellow-400">Agendada:</span>{' '}
                            {new Date(session.scheduled_for).toLocaleString('pt-PT')}
                          </p>
                        )}
                        <p>
                          <span className="font-medium text-slate-300">Participantes:</span> {session.participant_count}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Metrics + Logs Panel */}
          <div className="space-y-4">
            {/* Session Metrics */}
            {selectedSession && (
              <Card className="bg-slate-900 border-slate-800" data-testid="metrics-panel-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-['Manrope'] text-lg text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                    Metricas - {selectedSession.code}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingMetrics ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  ) : metrics ? (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="bg-slate-800 rounded-lg p-3 border border-slate-700" data-testid="metric-avg-latency">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Activity className="w-3.5 h-3.5 text-green-400" />
                          <span className="font-['Inter'] text-xs text-slate-400">Latencia Media</span>
                        </div>
                        <span className="font-['JetBrains_Mono'] text-xl font-bold text-white">
                          {metrics.avg_latency > 0 ? `${metrics.avg_latency}s` : '—'}
                        </span>
                        {metrics.min_latency > 0 && (
                          <p className="font-['Inter'] text-[10px] text-slate-500 mt-0.5">
                            Min: {metrics.min_latency}s · Max: {metrics.max_latency}s
                          </p>
                        )}
                      </div>
                      <div className="bg-slate-800 rounded-lg p-3 border border-slate-700" data-testid="metric-participants">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Users className="w-3.5 h-3.5 text-blue-400" />
                          <span className="font-['Inter'] text-xs text-slate-400">Participantes</span>
                        </div>
                        <span className="font-['JetBrains_Mono'] text-xl font-bold text-white">
                          {metrics.current_participants}
                        </span>
                        <p className="font-['Inter'] text-[10px] text-slate-500 mt-0.5">
                          Pico: {metrics.peak_participants} · Unicos: {metrics.unique_participants}
                        </p>
                      </div>
                      <div className="bg-slate-800 rounded-lg p-3 border border-slate-700" data-testid="metric-duration">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Clock className="w-3.5 h-3.5 text-amber-400" />
                          <span className="font-['Inter'] text-xs text-slate-400">Duracao</span>
                        </div>
                        <span className="font-['JetBrains_Mono'] text-xl font-bold text-white">
                          {metrics.duration_minutes > 0 ? `${metrics.duration_minutes}m` : '—'}
                        </span>
                      </div>
                      <div className="bg-slate-800 rounded-lg p-3 border border-slate-700" data-testid="metric-subtitles">
                        <div className="flex items-center gap-1.5 mb-1">
                          <BarChart3 className="w-3.5 h-3.5 text-purple-400" />
                          <span className="font-['Inter'] text-xs text-slate-400">Legendas</span>
                        </div>
                        <span className="font-['JetBrains_Mono'] text-xl font-bold text-white">
                          {metrics.total_subtitles}
                        </span>
                        <p className="font-['Inter'] text-[10px] text-slate-500 mt-0.5">
                          Transcricoes: {metrics.transcript_count}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="font-['Inter'] text-sm text-slate-500 text-center py-2">Sem metricas disponiveis</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Logs Panel */}
            <Card className="bg-slate-900 border-slate-800" data-testid="logs-panel-card">
            <CardHeader>
              <CardTitle className="font-['Manrope'] text-xl text-white">Logs da Sessao</CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedSession ? (
                <p className="font-['Inter'] text-slate-400 text-center py-8" data-testid="select-session-message">
                  Selecione uma sessao para ver os logs
                </p>
              ) : loadingLogs ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                </div>
              ) : logs.length === 0 ? (
                <p className="font-['Inter'] text-slate-400 text-center py-8" data-testid="no-logs-message">
                  Nenhum log encontrado
                </p>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      className="p-3 bg-slate-800 rounded border border-slate-700"
                      data-testid="log-item"
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-['Inter'] text-sm font-semibold text-white" data-testid="log-event">
                          {log.event}
                        </span>
                        <span className="font-['Inter'] text-xs text-slate-400" data-testid="log-timestamp">
                          {new Date(log.timestamp).toLocaleTimeString('pt-PT')}
                        </span>
                      </div>
                      {log.latency && (
                        <p className="font-['Inter'] text-xs text-slate-400" data-testid="log-latency">
                          Latencia: {log.latency.toFixed(2)}s
                        </p>
                      )}
                      {log.error && (
                        <p className="font-['Inter'] text-xs text-red-400 mt-1" data-testid="log-error">
                          Erro: {log.error}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        </div>
        </>)}
      </div>
    </div>
  );
}
